import { CaptionSegment } from '../../services/api/songAdService';

/**
 * 詳細編集タイムライン（FB11）のUndo/Redo・テロップ編集の純ロジック。
 *
 * DOM操作（ドラッグイベント）から切り離してあるのは、jestで実際のmouseイベントを
 * シミュレートせずに「分割・クランプ・Undo/Redo」をテストできるようにするため
 * （マウスドラッグのjsdomシミュレーションは壊れやすい）。React側は px⇔秒 の変換と
 * mousemove/mouseupの配線だけを担い、実際の値計算はここに全部寄せる。
 */

/** backendの SongAdTimelineService::MIN_CAPTION_SECONDS と同値（未満は保存時に無言で捨てられる） */
export const MIN_CAPTION_SECONDS = 0.2;

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

export function generateCaptionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `cap-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** テロップ本体をドラッグして移動（長さは維持、0〜totalDurationにクランプ） */
export function moveCaption(caption: CaptionSegment, deltaSec: number, totalDuration: number): CaptionSegment {
  const length = caption.end - caption.start;
  const start = clamp(caption.start + deltaSec, 0, Math.max(0, totalDuration - length));
  return { ...caption, start: round3(start), end: round3(start + length) };
}

/** 左端ハンドルのドラッグ。終了時刻 - MIN_CAPTION_SECONDS を超えて右には動かせない */
export function resizeCaptionStart(caption: CaptionSegment, newStart: number): CaptionSegment {
  const start = clamp(newStart, 0, caption.end - MIN_CAPTION_SECONDS);
  return { ...caption, start: round3(start) };
}

/** 右端ハンドルのドラッグ。開始時刻 + MIN_CAPTION_SECONDS を下回れない・totalDurationを超えない */
export function resizeCaptionEnd(caption: CaptionSegment, newEnd: number, totalDuration: number): CaptionSegment {
  const end = clamp(newEnd, caption.start + MIN_CAPTION_SECONDS, totalDuration);
  return { ...caption, end: round3(end) };
}

/**
 * カーソル位置（文字インデックス）でテロップを2つに分割する（spec §16.4）。
 * 表示時間は分割後の文言の文字数比で按分する（mockup.htmlの分割ロジックを踏襲）。
 * 前後どちらかが空文字になる位置では分割できない（null を返す）。
 */
export function splitCaptionAt(caption: CaptionSegment, cursorIndex: number): [CaptionSegment, CaptionSegment] | null {
  const text1 = caption.text.slice(0, cursorIndex).trim();
  const text2 = caption.text.slice(cursorIndex).trim();
  if (!text1 || !text2) return null;

  const total = caption.end - caption.start;
  const ratio = text1.length / (text1.length + text2.length);
  const mid = round3(caption.start + total * ratio);

  const first: CaptionSegment = { ...caption, text: text1, end: mid };
  const second: CaptionSegment = { ...caption, id: generateCaptionId(), text: text2, start: mid };
  return [first, second];
}

/** 現在の再生位置から2秒間のデフォルトテロップを作る（spec §16.5の簡易版。追加ボタン用） */
export function createCaptionAt(currentSec: number, totalDuration: number): CaptionSegment {
  const start = clamp(currentSec, 0, Math.max(0, totalDuration - MIN_CAPTION_SECONDS));
  const end = clamp(start + 2, start + MIN_CAPTION_SECONDS, totalDuration);
  return {
    id: generateCaptionId(),
    text: '新しいテロップ',
    start: round3(start),
    end: round3(end),
    style: 'simple',
    position: 'bottom',
    // v6 F3/r: 既定サイズを 中(md) → 大(lg) に変更（9:16で 46px→56px、約+20%）
    size: 'lg',
  };
}

/* ------------------------------------------------------------------ */
/* Undo/Redo履歴（テロップ配列のスナップショットスタック。上限50）           */
/* ------------------------------------------------------------------ */

export interface CaptionHistoryState {
  past: CaptionSegment[][];
  present: CaptionSegment[];
  future: CaptionSegment[][];
}

export type CaptionHistoryAction =
  | { type: 'commit'; captions: CaptionSegment[] }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'reset'; captions: CaptionSegment[] };

export const MAX_HISTORY = 50;

export function initCaptionHistory(captions: CaptionSegment[]): CaptionHistoryState {
  return { past: [], present: captions, future: [] };
}

export function captionHistoryReducer(state: CaptionHistoryState, action: CaptionHistoryAction): CaptionHistoryState {
  switch (action.type) {
    case 'commit': {
      const past = [...state.past, state.present].slice(-MAX_HISTORY);
      return { past, present: action.captions, future: [] };
    }
    case 'undo': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      return {
        past: state.past.slice(0, -1),
        present: previous,
        future: [state.present, ...state.future],
      };
    }
    case 'redo': {
      if (state.future.length === 0) return state;
      const [next, ...rest] = state.future;
      return {
        past: [...state.past, state.present].slice(-MAX_HISTORY),
        present: next,
        future: rest,
      };
    }
    case 'reset':
      return initCaptionHistory(action.captions);
    default:
      return state;
  }
}
